/**
 * Checks \ref, \cite and \label keys against the project index as you type:
 * unknown keys and duplicate labels are underlined, hovering a key previews
 * what it points at, and Ctrl/⌘-click jumps to its definition.
 */
import { forceLinting, linter, type Action, type Diagnostic } from "@codemirror/lint";
import type { EditorState, Extension } from "@codemirror/state";
import { EditorView, hoverTooltip } from "@codemirror/view";
import type { ProjectIndex } from "../lib/api";

export interface RefsOptions {
  getIndex: () => ProjectIndex | null;
  /** Project path of the file being edited. */
  getPath: () => string;
  onOpen: (file: string, line: number) => void;
}

type Kind = "ref" | "cite" | "label";
interface KeyUse {
  kind: Kind;
  key: string;
  from: number;
  to: number;
}

const PATTERNS: Array<[Kind, RegExp]> = [
  ["ref", /\\(?:ref|eqref|pageref|autoref|Autoref|nameref|Nameref|cref|Cref|cpageref|Cpageref|labelcref|vref|Vref|vpageref|subref|autopageref|zcref)\*?\s*\{([^}]*)\}/g],
  ["cite", /\\(?:[a-zA-Z]*cite[a-zA-Z]*|nocite)\*?\s*(?:\[[^\]]*\]\s*){0,2}\{([^}]*)\}/g],
  ["label", /\\label\s*\{([^}]*)\}/g],
];
const LINTED = /\.(tex|ltx|sty|cls|tikz)$/i;
const isMac = /Mac|iPhone|iPad/.test(navigator.platform);

/** Where a line's code ends and its % comment starts. */
function codeEnd(text: string): number {
  for (let i = 0; i < text.length; i++) {
    if (text[i] === "\\") i++;
    else if (text[i] === "%") return i;
  }
  return text.length;
}

function scanLine(text: string, offset: number, out: KeyUse[]) {
  const code = text.slice(0, codeEnd(text));
  if (!code.includes("\\")) return;
  for (const [kind, re] of PATTERNS) {
    for (const m of code.matchAll(re)) {
      const group = m[1];
      let pos = m.index + m[0].length - 1 - group.length;
      for (const part of group.split(",")) {
        const key = part.trim();
        const from = offset + pos + part.length - part.trimStart().length;
        // Skip macro arguments (#1), keys built from commands, and \nocite{*}.
        if (key && !/[#\\*]/.test(key)) out.push({ kind, key, from, to: from + key.length });
        pos += part.length + 1;
      }
    }
  }
}

function scanDoc(state: EditorState): KeyUse[] {
  const out: KeyUse[] = [];
  for (let n = 1; n <= state.doc.lines; n++) {
    const line = state.doc.line(n);
    scanLine(line.text, line.from, out);
  }
  return out;
}

function keyAt(state: EditorState, pos: number): KeyUse | null {
  const line = state.doc.lineAt(pos);
  const out: KeyUse[] = [];
  scanLine(line.text, line.from, out);
  return out.find((u) => pos >= u.from && pos <= u.to) ?? null;
}

/** The closest candidate to a misspelt key, if it's close enough to be a likely typo. */
function closest(key: string, candidates: Iterable<string>): string | null {
  const limit = Math.min(3, Math.max(1, Math.floor(key.length / 4)));
  let best: string | null = null;
  let bestD = limit + 1;
  for (const c of candidates) {
    if (Math.abs(c.length - key.length) >= bestD) continue;
    const d = distance(key, c, bestD);
    if (d < bestD) {
      best = c;
      bestD = d;
    }
  }
  return best;
}

/** Edit distance where swapping two adjacent letters counts as one typo; gives up once it reaches max. */
function distance(a: string, b: string, max: number): number {
  let before: number[] = [];
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) cur[j] = Math.min(cur[j], before[j - 2] + 1);
      rowMin = Math.min(rowMin, cur[j]);
    }
    if (rowMin >= max) return max;
    before = prev;
    prev = cur;
  }
  return prev[b.length];
}

/** Everything the index and the live text say about the keys in this file. */
function lookups(state: EditorState, opts: RefsOptions, index: ProjectIndex) {
  const path = opts.getPath();
  const uses = scanDoc(state);
  // Labels in this file come from the live text; the index may be a few seconds behind.
  const here = new Map<string, number[]>();
  for (const u of uses) if (u.kind === "label") here.set(u.key, [...(here.get(u.key) ?? []), u.from]);
  const elsewhere = new Map<string, ProjectIndex["labels"][number]>();
  for (const l of index.labels) if (l.file !== path && !elsewhere.has(l.label)) elsewhere.set(l.label, l);
  const bib = new Map<string, ProjectIndex["citations"][number]>();
  for (const c of index.citations) if (!bib.has(c.key)) bib.set(c.key, c);
  return {
    path,
    uses,
    here,
    elsewhere,
    bib,
    compiledLabels: new Set(index.compiled?.labels ?? []),
    compiledCites: new Set(index.compiled?.citations ?? []),
    hereLabel: (key: string) => index.labels.find((l) => l.file === path && l.label === key),
  };
}

function lintSource(opts: RefsOptions) {
  return (view: EditorView): Diagnostic[] => {
    const index = opts.getIndex();
    if (!index || !LINTED.test(opts.getPath())) return [];
    const k = lookups(view.state, opts, index);
    // Without any bibliography in the project, every \cite would be flagged; the compile log covers that case.
    const checkCites = k.bib.size + k.compiledCites.size > 0;
    const canFix = !view.state.readOnly;
    const fix = (key: string | null): Action[] =>
      key && canFix ? [{ name: `Change to ${key}`, apply: (v, from, to) => v.dispatch({ changes: { from, to, insert: key } }) }] : [];
    const out: Diagnostic[] = [];
    for (const u of k.uses) {
      if (u.kind === "ref") {
        if (k.here.has(u.key) || k.elsewhere.has(u.key) || k.compiledLabels.has(u.key)) continue;
        out.push({
          from: u.from,
          to: u.to,
          severity: "warning",
          source: "references",
          message: `No \\label{${u.key}} in this project.`,
          actions: fix(closest(u.key, new Set([...k.here.keys(), ...k.elsewhere.keys()]))),
        });
      } else if (u.kind === "cite") {
        if (!checkCites || k.bib.has(u.key) || k.compiledCites.has(u.key)) continue;
        out.push({
          from: u.from,
          to: u.to,
          severity: "warning",
          source: "references",
          message: `No bibliography entry with the key “${u.key}”.`,
          actions: fix(closest(u.key, k.bib.keys())),
        });
      } else {
        const other = k.elsewhere.get(u.key);
        if ((k.here.get(u.key)?.length ?? 0) > 1) {
          out.push({ from: u.from, to: u.to, severity: "warning", source: "references", message: `\\label{${u.key}} is defined more than once in this file.` });
        } else if (other) {
          out.push({
            from: u.from,
            to: u.to,
            severity: "warning",
            source: "references",
            message: `\\label{${u.key}} is also defined in ${other.file}:${other.line}.`,
            actions: [{ name: "Go there", apply: () => opts.onOpen(other.file, other.line) }],
          });
        }
      }
    }
    return out;
  };
}

interface Target {
  kind: string;
  key: string;
  file?: string;
  line?: number;
  lines: string[];
  /** A bibliography entry's title. */
  title?: string;
  note?: string;
}

/** What a \ref or \cite key points at, or null when it's undefined (the lint message covers that). */
function resolve(state: EditorState, use: KeyUse, opts: RefsOptions, index: ProjectIndex): Target | null {
  const k = lookups(state, opts, index);
  if (use.kind === "ref") {
    const here = k.here.get(use.key);
    if (here) {
      const line = state.doc.lineAt(here[0]);
      const context = k.hereLabel(use.key)?.context ?? line.text.replace(/\\label\{[^}]*\}/g, "").trim();
      return { kind: "label", key: use.key, file: k.path, line: line.number, lines: context ? [context] : [] };
    }
    const other = k.elsewhere.get(use.key);
    if (other) return { kind: "label", key: use.key, file: other.file, line: other.line, lines: other.context ? [other.context] : [] };
    if (k.compiledLabels.has(use.key)) return { kind: "label", key: use.key, lines: [], note: "Defined during the compile (by a package or macro), not in the source." };
    return null;
  }
  if (use.kind === "cite") {
    const entry = k.bib.get(use.key);
    if (entry) {
      const byline = [entry.author, entry.year && `(${entry.year})`].filter(Boolean).join(" ");
      return { kind: entry.type, key: use.key, file: entry.file, line: entry.line, lines: byline ? [byline] : [], title: entry.title };
    }
    if (k.compiledCites.has(use.key)) return { kind: "citation", key: use.key, lines: [], note: "Found by the last compile, but not in this project's .bib files." };
    return null;
  }
  return null;
}

function tooltipDom(t: Target, opts: RefsOptions): HTMLElement {
  const dom = document.createElement("div");
  dom.className = "cm-ref-tip";
  const head = dom.appendChild(document.createElement("div"));
  head.className = "cm-ref-head";
  const kind = head.appendChild(document.createElement("span"));
  kind.className = "cm-ref-kind";
  kind.textContent = t.kind;
  const key = head.appendChild(document.createElement("code"));
  key.textContent = t.key;
  if (t.file) {
    const loc = head.appendChild(document.createElement("button"));
    loc.className = "cm-ref-loc";
    loc.textContent = t.line ? `${t.file}:${t.line}` : t.file;
    loc.title = `Open (or ${isMac ? "⌘" : "Ctrl"}-click the key)`;
    loc.onmousedown = (e) => e.preventDefault();
    loc.onclick = () => opts.onOpen(t.file!, t.line ?? 1);
  }
  const add = (className: string, text: string | undefined) => {
    if (!text) return;
    const p = dom.appendChild(document.createElement("div"));
    p.className = className;
    p.textContent = text;
  };
  for (const text of t.lines) add("cm-ref-text", text);
  add("cm-ref-title", t.title);
  add("cm-ref-note", t.note);
  return dom;
}

export function references(opts: RefsOptions): Extension {
  return [
    linter(lintSource(opts), { delay: 500 }),
    hoverTooltip(
      (view, pos) => {
        const index = opts.getIndex();
        const use = index && keyAt(view.state, pos);
        const target = use && resolve(view.state, use, opts, index);
        if (!use || !target) return null;
        return { pos: use.from, end: use.to, above: true, create: () => ({ dom: tooltipDom(target, opts) }) };
      },
      { hoverTime: 350 },
    ),
    EditorView.domEventHandlers({
      mousedown(e, view) {
        if (e.button !== 0 || !(isMac ? e.metaKey : e.ctrlKey) || e.altKey || e.shiftKey) return false;
        const index = opts.getIndex();
        const pos = view.posAtCoords({ x: e.clientX, y: e.clientY });
        const use = index && pos !== null ? keyAt(view.state, pos) : null;
        const target = use && resolve(view.state, use, opts, index!);
        if (!target?.file) return false;
        e.preventDefault();
        opts.onOpen(target.file, target.line ?? 1);
        return true;
      },
    }),
  ];
}

/** Re-check the open file, e.g. after the project index changed. */
export const recheckReferences = forceLinting;
